import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaginationControls } from "@/components/PaginationControls";
import { cardImageStyle } from "@/components/Card3D";
import { ArenaStatChips, AttributeChips, ArenaProfile, squadPower } from "@/components/ArenaBits";
import { BareGearWarning, isBareOfGear } from "@/components/BareGearWarning";
import { Coins, Search, X, Swords, Flame, Dices, ShieldHalf, ChevronLeft, ChevronRight, Layers, AlertTriangle, Ticket, Skull, Sparkles, Check, Gauge, Sliders, Pencil, Package } from "lucide-react";
import { cn } from "@/lib/utils";

const RARITIES = ["all", "common", "uncommon", "rare", "epic", "legendary", "goddess"];
const RARITY_HEX = { common: "#a1a1aa", uncommon: "#34d399", rare: "#38bdf8", epic: "#e879f9", legendary: "#fbbf24", goddess: "#fb7185" };

// Danger ramp for the four PvC difficulties (Rookie → Nightmare), keyed by order.
const DIFF_TONE = [
  { accent: "#34d399", ring: "border-emerald-400/70", bg: "bg-emerald-400/[0.07]", text: "text-emerald-200", glow: "rgba(52,211,153,0.30)" },
  { accent: "#fbbf24", ring: "border-amber-400/70", bg: "bg-amber-400/[0.07]", text: "text-amber-200", glow: "rgba(251,191,36,0.30)" },
  { accent: "#fb923c", ring: "border-orange-400/70", bg: "bg-orange-400/[0.07]", text: "text-orange-200", glow: "rgba(251,146,60,0.32)" },
  { accent: "#fb7185", ring: "border-rose-400/70", bg: "bg-rose-400/[0.07]", text: "text-rose-200", glow: "rgba(251,113,133,0.36)" },
];

function RosterTile({ editions, activeIdx, onCycle, slotIndex, onClick }) {
  const card = editions[activeIdx] || editions[0];
  const selected = slotIndex >= 0;
  const multi = editions.length > 1;
  return (
    <HoverCard openDelay={120} closeDelay={60}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          data-testid={`squad-pick-${card.id}`}
          onClick={onClick}
          className={cn(
            "relative rounded-lg overflow-hidden border text-left w-full transition-all duration-150",
            selected ? "border-cyan-400 ring-2 ring-cyan-400/50 scale-[0.97]" : "border-white/10 hover:border-white/35 hover:-translate-y-0.5"
          )}
        >
          <div className={`relative ${card.orientation === "landscape" ? "aspect-[3.5/2.5]" : "aspect-[2.5/3.5]"} rarity-${card.rarity} bg-black`}>
            {card.front_image && <img src={card.front_image} alt={card.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full" style={cardImageStyle(card)} draggable={false} />}
            <div className="foil-overlay" />
            {selected && (
              <div className="absolute top-1 right-1 h-6 w-6 rounded-full bg-cyan-400 text-black flex items-center justify-center font-display text-sm shadow-[0_0_10px_rgba(34,211,238,0.7)]">
                {slotIndex + 1}
              </div>
            )}
            {multi && (
              <div className="absolute top-1 left-1 inline-flex items-center gap-0.5 rounded-full bg-black/70 border border-white/15 px-1 py-0.5 font-mono text-[8px] text-white/80" data-testid={`edition-badge-${card.template_id}`}>
                <Layers className="h-2.5 w-2.5" /> {editions.length}
              </div>
            )}
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent px-1.5 pb-1 pt-4">
              <div className="font-mono text-[9px] text-white/85 truncate">{card.title}</div>
              <div className="flex items-center justify-between gap-1">
                <span className="flex items-center gap-1 font-mono text-[9px] text-amber-300">
                  <Flame className="h-2.5 w-2.5" /> {squadPower(card.arena)}
                </span>
                {multi && (
                  <span className="inline-flex items-center gap-0.5" data-testid={`edition-selector-${card.template_id}`}>
                    <span role="button" tabIndex={-1} data-testid={`edition-prev-${card.template_id}`}
                      onClick={(e) => { e.stopPropagation(); onCycle(-1); }}
                      className="h-4 w-4 rounded bg-black/60 border border-white/15 flex items-center justify-center text-white/70 hover:text-cyan-300">
                      <ChevronLeft className="h-3 w-3" />
                    </span>
                    <span className="font-mono text-[8px] text-white/60 w-7 text-center">#{card.serial ? card.serial.slice(-3) : activeIdx + 1}</span>
                    <span role="button" tabIndex={-1} data-testid={`edition-next-${card.template_id}`}
                      onClick={(e) => { e.stopPropagation(); onCycle(1); }}
                      className="h-4 w-4 rounded bg-black/60 border border-white/15 flex items-center justify-center text-white/70 hover:text-cyan-300">
                      <ChevronRight className="h-3 w-3" />
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start" sideOffset={10} className="w-64 bg-[hsl(222_24%_8%)] border-white/15 z-[80] pointer-events-none" data-testid={`roster-hover-${card.id}`}>
        <div className="flex gap-2.5">
          <div className={`relative w-20 ${card.orientation === "landscape" ? "aspect-[3.5/2.5] self-start" : "aspect-[2.5/3.5]"} rounded-md overflow-hidden bg-black shrink-0 rarity-${card.rarity}`}>
            {card.front_image && <img src={card.front_image} alt={card.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(card)} draggable={false} />}
            <div className="foil-overlay" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm text-white leading-tight">{card.title}</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">{card.rarity}{card.serial ? ` · ${card.serial}` : ""}</div>
            {multi && <div className="font-mono text-[9px] text-cyan-300/80 mt-0.5">Edition {activeIdx + 1} of {editions.length}</div>}
          </div>
        </div>
        <ArenaProfile arena={card.arena} card={card} className="mt-2" />
      </HoverCardContent>
    </HoverCard>
  );
}

function SquadSlot({ card, index, onRemove }) {
  if (!card) {
    return (
      <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-4 flex items-center justify-center min-h-[88px]" data-testid={`squad-slot-empty-${index}`}>
        <div className="text-center">
          <div className="font-display text-lg text-white/25">SLOT {index + 1}</div>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/20">Pick a soldier</div>
        </div>
      </div>
    );
  }
  return (
    <div className={`relative rounded-xl border border-cyan-400/30 bg-[hsl(220_18%_9%)] p-2.5 rarity-${card.rarity}`} data-testid={`squad-slot-${index}`} style={{ boxShadow: "0 0 18px rgba(34,211,238,0.08)" }}>
      <button type="button" onClick={onRemove} data-testid={`squad-slot-remove-${index}`} className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-black/60 border border-white/15 flex items-center justify-center text-white/60 hover:text-white z-10">
        <X className="h-3 w-3" />
      </button>
      <div className="flex gap-2.5">
        <div className={`relative w-14 ${card.orientation === "landscape" ? "h-10 self-center" : "h-20"} rounded-md overflow-hidden bg-black shrink-0`}>
          {card.front_image && <img src={card.front_image} alt={card.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(card)} draggable={false} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm text-white truncate pr-5">{card.title}</div>
          <ArenaStatChips arena={card.arena} compact className="mt-1" />
          <AttributeChips attributes={card.arena?.attributes} className="mt-1" />
          <div className="mt-1 font-mono text-[9px] text-cyan-300/70">{(card.arena?.moves || []).map((m) => m.name).join(" · ")}</div>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// WAR ROOM — the soldier select. Roster on the left (search + rarity
// filter + power sort), three glowing squad slots on the right with
// full attribute/move readouts and a live Squad Power total.
// ===================================================================
export function SquadPicker({
  title, confirmLabel, showWager = true, wagerLocked = null, feeNote,
  busy, headerSlot, onConfirm, onClose, confirmExtraDisabled = false,
  showBuffs = false, buffsAvailable = [], onRerollBuffs, showStake = false, stakeRequired = null,
  showDifficulty = false, difficulties = [], showPowerCap = false, powerCap = null,
  warning = null, preselectId = null, showToken = false, warnBareGear = false,
}) {
  const [selected, setSelected] = useState([]); // card ids in slot order
  const [editingRoster, setEditingRoster] = useState(false);
  const [wager, setWager] = useState(0);
  const [useToken, setUseToken] = useState(false);
  const [q, setQ] = useState("");
  const [rarity, setRarity] = useState("all");
  const [buffSel, setBuffSel] = useState([]);
  const [capEnabled, setCapEnabled] = useState(false);
  const [capPct, setCapPct] = useState(25);
  const [stakeSel, setStakeSel] = useState([]);
  const [diffId, setDiffId] = useState(null);
  const { data: myCards } = useQuery({
    queryKey: ["my-cards"],
    queryFn: async () => (await api.get("/cards/mine")).data,
  });
  const { data: me } = useQuery({
    queryKey: ["me-balance"],
    queryFn: async () => (await api.get("/auth/me")).data,
    refetchInterval: 8000,
  });
  const balance = me?.credits ?? 0;
  // Per-difficulty wager caps come from the API (`wager_min`/`wager_max`);
  // fall back to legacy defaults if the difficulty list hasn't loaded yet.
  const diffMin = showDifficulty ? Number(difficulties.find((d) => d.id === diffId)?.wager_min ?? 100) : 100;
  const diffMax = showDifficulty ? Number(difficulties.find((d) => d.id === diffId)?.wager_max ?? 2000) : 2000;
  const PVC_WAGER_CAP = diffMax;
  const PVC_MIN_WAGER = diffMin;
  const maxWager = showDifficulty ? Math.min(balance, PVC_WAGER_CAP) : balance;

  // Arena Tokens — a free no-wager battle. Spend one instead of staking credits.
  const { data: inv } = useQuery({
    queryKey: ["inventory"],
    queryFn: async () => (await api.get("/inventory")).data,
    enabled: showToken,
  });
  const arenaTokens = (inv?.tokens || []).find((t) => t.type === "arena_token")?.qty || 0;
  const tokenActive = showToken && useToken && arenaTokens > 0;

  const isLockedElsewhere = (c) => {
    if (c.locked_out || c.locked_in_show || c.locked_in_request || c.locked_in_commission || c.locked_in_cavern) return true;
    if (c.shamed_until) {
      const t = new Date(c.shamed_until).getTime();
      if (!Number.isNaN(t) && t > Date.now()) return true;
    }
    return false;
  };
  const busyCount = useMemo(() => (myCards || []).filter(isLockedElsewhere).length, [myCards]);
  const roster = useMemo(() => (myCards || [])
    .filter((c) => !isLockedElsewhere(c))
    .filter((c) => rarity === "all" || c.rarity === rarity)
    .filter((c) => !q || (c.title || "").toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => squadPower(b.arena) - squadPower(a.arena)), [myCards, q, rarity]);

  // Group editions of the same card (same template) into one tile + a selector.
  const groups = useMemo(() => {
    const m = new Map();
    for (const c of roster) {
      const k = c.template_id || c.id;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(c);
    }
    return Array.from(m.entries()).map(([key, editions]) => ({ key, editions }));
  }, [roster]);
  const [editionPick, setEditionPick] = useState({}); // template key -> active edition index
  const cycleEdition = (key, len, dir) =>
    setEditionPick((p) => ({ ...p, [key]: (((p[key] || 0) + dir) % len + len) % len }));

  // Selected soldiers carrying neither gear nor consumables — entering bare risks the card.
  const bareNames = useMemo(() => {
    if (!warnBareGear || !selected.length) return [];
    const byId = new Map((myCards || []).map((c) => [c.id, c]));
    return selected
      .map((id) => byId.get(id))
      .filter((c) => c && isBareOfGear(c))
      .map((c) => c.title || "Untitled");
  }, [warnBareGear, selected, myCards]);

  // Paginate the roster so big collections stay browsable.
  const [rosterPage, setRosterPage] = useState(0);
  const [rosterPageSize, setRosterPageSize] = useState(25);
  useEffect(() => { setRosterPage(0); }, [q, rarity]);
  const pagedGroups = useMemo(
    () => groups.slice(rosterPage * rosterPageSize, rosterPage * rosterPageSize + rosterPageSize),
    [groups, rosterPage, rosterPageSize]);

  const byId = useMemo(() => new Map((myCards || []).map((c) => [c.id, c])), [myCards]);
  const squad = selected.map((id) => byId.get(id)).filter(Boolean);
  // Collapse-on-select: once the 3-soldier squad is full, fold the roster away
  // (like Race/Cavern) so the focus shifts to difficulty + modifiers. Dropping a
  // soldier (or tapping "Change squad") re-opens it.
  const squadFull = selected.length >= 3;
  const rosterCollapsed = squadFull && !editingRoster;
  useEffect(() => { if (selected.length < 3) setEditingRoster(false); }, [selected.length]);

  useEffect(() => {
    if (preselectId && byId.has(preselectId)) {
      setSelected((s) => (s.includes(preselectId) ? s : [preselectId, ...s]));
    }
  }, [preselectId, byId]); // eslint-disable-line react-hooks/exhaustive-deps
  const totalPower = squad.reduce((s, c) => s + squadPower(c.arena), 0);

  const toggle = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= 3 ? prev : [...prev, id]);
  const rawWagerNum = tokenActive ? 0 : (wagerLocked != null ? wagerLocked : (Number(wager) || 0));
  const effWager = rawWagerNum;

  const unlockedRoster = useMemo(() => (myCards || [])
    .filter((c) => !c.locked_out && !c.locked_in_market && !c.locked_in_trade && !c.locked_in_battle), [myCards]);
  const toggleStake = (id) =>
    setStakeSel((prev) => prev.includes(id)
      ? prev.filter((x) => x !== id)
      : (stakeRequired != null && prev.length >= stakeRequired) ? prev : [...prev, id]);
  const toggleBuff = (id) =>
    setBuffSel((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const stakeOk = stakeRequired == null || stakeSel.length === stakeRequired;
  // For PvC: 0 is always a valid practice run; any positive value must sit
  // between the difficulty's min and max. Values above the max or below
  // min-but-non-zero DISABLE start rather than silently clamping — the user
  // asked for a hard gate ("if they try and start with more or less than these
  // limits, it removes the start button").
  const wagerOverMax = showDifficulty && !tokenActive && effWager > PVC_WAGER_CAP;
  const wagerBelowMin = showDifficulty && !tokenActive && effWager > 0 && effWager < PVC_MIN_WAGER;
  const wagerOutOfRange = wagerOverMax || wagerBelowMin;

  // PvC difficulty + modifier-scaled wager math.
  const selectedDiff = difficulties.find((d) => d.id === diffId)
    || difficulties.find((d) => d.id === "veteran")
    || difficulties[0];
  // Difficulty gates how many modifiers are offered: Rookie shows 1 line, Nightmare all.
  const buffSlots = showBuffs ? (selectedDiff?.modifier_slots ?? buffsAvailable.length) : buffsAvailable.length;
  const visibleBuffs = buffsAvailable.slice(0, buffSlots);
  const visibleBuffIds = new Set(visibleBuffs.map((b) => b.id));
  const activeBuffSel = tokenActive ? [] : buffSel.filter((id) => visibleBuffIds.has(id));
  // Auto-prune: dropping to a lower difficulty (fewer slots) clears the now-locked modifiers.
  useEffect(() => {
    if (!showBuffs) return;
    const allowed = new Set(buffsAvailable.slice(0, buffSlots).map((b) => b.id));
    setBuffSel((prev) => (prev.some((id) => !allowed.has(id)) ? prev.filter((id) => allowed.has(id)) : prev));
  }, [buffSlots, buffsAvailable, showBuffs]);
  const buffCost = activeBuffSel.reduce((s, id) => s + (buffsAvailable.find((b) => b.id === id)?.cost || 0), 0);
  const baseWager = tokenActive ? 0 : (wagerLocked != null ? wagerLocked : (Number(wager) || 0));
  const effectiveWager = Math.max(0, baseWager + (showBuffs ? buffCost : 0));
  // PvC no longer pays cash — a win lets the player spend their wager on loot
  // (cards/packs/tokens at half store price) of the difficulty's tier.
  const lootRarity = selectedDiff?.card_rarity || "common";

  // Power-cap: creator preview of the max opponent power they'll allow; joiner gate.
  const capMaxForOthers = Math.round(totalPower * (1 + capPct / 100));
  const capExceeded = !!powerCap && totalPower > powerCap.maxPower;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(222_22%_7%)] border-white/10 max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[0.08em] flex items-center gap-2">
            <Swords className="h-5 w-5 text-cyan-300" /> {title}
            <span className="ml-auto font-mono text-xs text-amber-300 inline-flex items-center gap-1" data-testid="squad-power-total">
              <Flame className="h-3.5 w-3.5" /> {totalPower.toLocaleString()} SQUAD PWR
            </span>
          </DialogTitle>
        </DialogHeader>
        {headerSlot}

        {warning && (
          <div data-testid="squad-forfeit-warning" className="rounded-2xl border-2 border-rose-500/60 bg-gradient-to-r from-rose-500/[0.16] to-amber-500/[0.10] p-4 sm:p-5 flex items-start gap-3.5 shadow-[0_0_28px_rgba(244,63,94,0.25)]">
            <AlertTriangle className="h-8 w-8 sm:h-9 sm:w-9 text-rose-400 shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <div className="font-display tracking-[0.06em] text-rose-200 text-lg sm:text-xl">{warning.title}</div>
              <div className="text-white/85 text-sm sm:text-base mt-1">{warning.body}</div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-[1fr_320px] gap-4">
          {/* Roster */}
          <div>
            {/* Collapse-on-select strip — shown once the 3-soldier squad is locked. */}
            <AnimatePresence initial={false}>
              {rosterCollapsed && (
                <motion.div
                  key="squad-locked"
                  initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  data-testid="squad-locked-strip"
                  className="mb-3 rounded-2xl border border-cyan-400/30 bg-gradient-to-r from-cyan-400/[0.08] to-transparent p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300 inline-flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5" /> Squad locked
                    </span>
                    <button type="button" data-testid="squad-edit-button" onClick={() => setEditingRoster(true)}
                      className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-white/60 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                      <Pencil className="h-3 w-3" /> Change
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {squad.map((c, i) => (
                      <div key={c.id} className={`relative rounded-lg overflow-hidden border border-cyan-400/30 ${c.orientation === "landscape" ? "aspect-[3.5/2.5]" : "aspect-[2.5/3.5]"} bg-black rarity-${c.rarity}`} data-testid={`squad-locked-${i}`}>
                        {c.front_image && <img src={c.front_image} alt={c.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(c)} draggable={false} />}
                        <div className="foil-overlay" />
                        <div className="absolute top-1 right-1 h-5 w-5 rounded-full bg-cyan-400 text-black flex items-center justify-center font-display text-xs shadow-[0_0_8px_rgba(34,211,238,0.7)]">{i + 1}</div>
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-1.5 pb-1 pt-4">
                          <div className="font-mono text-[9px] text-white/90 truncate">{c.title}</div>
                          <div className="font-mono text-[8px] text-amber-300 inline-flex items-center gap-0.5"><Flame className="h-2.5 w-2.5" />{squadPower(c.arena)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {!rosterCollapsed && (<>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <div className="relative flex-1 min-w-[160px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
                <Input data-testid="squad-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search soldiers…" className="pl-8 h-8 text-sm bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
              <div className="flex gap-1 overflow-x-auto">
                {RARITIES.map((r) => (
                  <button key={r} type="button" data-testid={`squad-filter-${r}`} onClick={() => setRarity(r)}
                    className={cn("px-2 py-1 rounded font-mono text-[9px] uppercase tracking-[0.15em] whitespace-nowrap border",
                      rarity === r ? "bg-white/10 text-white border-white/20" : "text-white/45 border-white/5 hover:text-white/80")}>
                    {r}
                  </button>
                ))}
              </div>
            </div>
            {busyCount > 0 && (
              <div data-testid="squad-busy-note" className="mb-2 font-mono text-[10px] text-white/35 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 shrink-0" /> {busyCount} soldier{busyCount === 1 ? "" : "s"} hidden — busy elsewhere (Talent Show, Cavern, request or commission).
              </div>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="squad-picker-grid">
              {pagedGroups.map((g) => {
                const idx = Math.min(editionPick[g.key] || 0, g.editions.length - 1);
                const active = g.editions[idx];
                return (
                  <RosterTile
                    key={g.key}
                    editions={g.editions}
                    activeIdx={idx}
                    slotIndex={selected.indexOf(active.id)}
                    onCycle={(dir) => cycleEdition(g.key, g.editions.length, dir)}
                    onClick={() => toggle(active.id)}
                  />
                );
              })}
              {groups.length === 0 && (
                <div className="col-span-full text-center text-white/40 text-sm py-10">
                  {(myCards || []).length === 0 ? "You don't own any cards yet — visit the Store first." : "No soldiers match your filters."}
                </div>
              )}
            </div>
            <PaginationControls
              page={rosterPage}
              total={groups.length}
              pageSize={rosterPageSize}
              onPageChange={setRosterPage}
              onPageSizeChange={(s) => { setRosterPageSize(s); setRosterPage(0); }}
              testidPrefix="squad-roster"
              className="!mt-3"
            />
            </>)}

            {showDifficulty && difficulties.length > 0 && (
              <div className="pt-4" data-testid="difficulty-section">
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5 text-cyan-300" /> Difficulty — tougher Machine, better loot</Label>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {difficulties.map((d, di) => {
                    const on = (selectedDiff?.id) === d.id;
                    const tone = DIFF_TONE[di] || DIFF_TONE[DIFF_TONE.length - 1];
                    const slots = d.modifier_slots >= 99 ? "All" : d.modifier_slots;
                    const lr = d.card_rarity || "common";
                    return (
                      <motion.button key={d.id} type="button" data-testid={`difficulty-${d.id}`} onClick={() => setDiffId(d.id)} title={d.desc}
                        whileHover={{ y: -3 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 400, damping: 26 }}
                        className={cn("relative overflow-hidden text-left rounded-xl border p-2.5 transition-colors",
                          on ? `${tone.ring} ${tone.bg}` : "border-white/10 hover:border-white/30 bg-white/[0.02]")}
                        style={on ? { boxShadow: `0 0 22px ${tone.glow}` } : undefined}>
                        {/* danger pips */}
                        <div className="flex items-center gap-0.5 mb-1.5">
                          {[0, 1, 2, 3].map((p) => (
                            <span key={p} className="h-1 flex-1 rounded-full" style={{ background: p <= di ? tone.accent : "rgba(255,255,255,0.10)" }} />
                          ))}
                        </div>
                        <div className="flex items-center gap-1">
                          {di === 3 && <Skull className="h-4 w-4 shrink-0" style={{ color: tone.accent }} />}
                          <span className={cn("font-display text-base leading-none", on ? tone.text : "text-white")}>{d.name}</span>
                        </div>
                        <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-white/40 mt-1">spend your wager on loot</div>
                        <div className="flex items-center justify-between gap-1 mt-2 pt-1.5 border-t border-white/8">
                          <span className="inline-flex items-center gap-1 font-mono text-[9px]" style={{ color: RARITY_HEX[lr] }}>
                            <Package className="h-2.5 w-2.5" /> {lr}
                          </span>
                          <span className="inline-flex items-center gap-0.5 font-mono text-[9px] text-white/45">
                            <Sliders className="h-2.5 w-2.5" /> {slots} mod{slots === 1 ? "" : "s"}
                          </span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}

            {showBuffs && buffsAvailable.length > 0 && (
              <div className="pt-4" data-testid="buff-section">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1.5"><Sliders className="h-3.5 w-3.5 text-violet-300" /> Modifiers</Label>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full border border-violet-400/30 bg-violet-400/10 px-2 py-0.5 font-mono text-[9px] text-violet-200" data-testid="buff-slots-note">
                      {activeBuffSel.length}/{visibleBuffs.length} active · {selectedDiff?.name}
                    </span>
                    {onRerollBuffs && (
                      <button type="button" data-testid="reroll-buffs-button" onClick={() => onRerollBuffs()}
                        className="inline-flex items-center gap-1 shrink-0 rounded-md border border-white/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-white/60 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                        <Dices className="h-3 w-3" /> Reroll
                      </button>
                    )}
                  </div>
                </div>
                <p className="font-mono text-[9px] text-rose-300/70 mb-2 inline-flex items-center gap-1"><Swords className="h-2.5 w-2.5" /> Each modifier you pick also arms the Machine with a random one.</p>
                <div className="grid grid-cols-1 gap-1.5">
                  {visibleBuffs.map((b) => {
                    const on = activeBuffSel.includes(b.id);
                    const good = b.cost >= 0; // a "good" (stronger) modifier raises your stake; handicaps refund.
                    return (
                      <motion.button key={b.id} type="button" data-testid={`buff-${b.id}`} onClick={() => toggleBuff(b.id)}
                        whileTap={{ scale: 0.99 }}
                        className={cn("relative w-full text-left rounded-xl border px-3 py-2.5 transition-colors flex items-center gap-3",
                          on ? (good ? "border-violet-400/60 bg-violet-400/[0.09]" : "border-emerald-400/60 bg-emerald-400/[0.09]") : "border-white/10 hover:border-white/30 bg-white/[0.02]")}>
                        <span className={cn("h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-colors",
                          on ? (good ? "bg-violet-400 border-violet-300" : "bg-emerald-400 border-emerald-300") : "border-white/20 bg-white/[0.03]")}>
                          {on && <Check className="h-3 w-3 text-black" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className={cn("font-display leading-tight transition-colors", on ? "text-base text-white" : "text-sm text-white")}>
                            {b.name}
                            <span className={cn("font-mono ml-1", on ? "text-xs text-white/70" : "text-[10px] text-white/45")}>— {b.desc}</span>
                          </div>
                          {b.opponent && (
                            <div className={cn("font-mono leading-tight flex items-center gap-1 mt-1", on ? "text-[11px] text-rose-300" : "text-[10px] text-rose-300/75")} data-testid={`buff-opponent-${b.id}`}>
                              <Swords className="h-3 w-3 shrink-0" /> Machine gets: {b.opponent.desc}
                            </div>
                          )}
                        </div>
                        <span className={cn("font-mono text-[12px] inline-flex items-center gap-0.5 shrink-0 rounded-md border px-1.5 py-0.5",
                          good ? "text-amber-300 border-amber-400/30 bg-amber-400/10" : "text-emerald-300 border-emerald-400/30 bg-emerald-400/10")}>
                          <Coins className="h-3.5 w-3.5" />{good ? `+${b.cost}` : b.cost}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Squad panel */}
          <div className="space-y-2">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">Your squad ({squad.length}/3)</div>
            {[0, 1, 2].map((i) => (
              <SquadSlot key={i} index={i} card={squad[i]} onRemove={() => toggle(selected[i])} />
            ))}
            {showWager && (
              <div className="pt-1" data-testid="wager-section">
                {showToken && (
                  <button
                    type="button"
                    data-testid="arena-token-toggle"
                    disabled={arenaTokens <= 0}
                    onClick={() => setUseToken((v) => !v)}
                    className={cn(
                      "w-full mb-2 rounded-xl border px-3 py-2.5 text-left transition-all flex items-center gap-3",
                      tokenActive
                        ? "border-rose-400/70 bg-rose-400/10 shadow-[0_0_18px_rgba(251,113,133,0.18)]"
                        : arenaTokens > 0
                          ? "border-white/12 bg-white/[0.03] hover:border-rose-400/40"
                          : "border-white/8 bg-white/[0.02] opacity-50 cursor-not-allowed"
                    )}
                  >
                    <span className={cn("h-8 w-8 rounded-lg flex items-center justify-center shrink-0 border",
                      tokenActive ? "bg-rose-400/20 border-rose-300/40" : "bg-white/5 border-white/10")}>
                      <Ticket className={cn("h-4 w-4", tokenActive ? "text-rose-300" : "text-white/55")} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-display text-sm text-white leading-tight">
                        Use Arena Token <span className="text-rose-300">— FREE battle</span>
                      </span>
                      <span className="block font-mono text-[10px] text-white/45 mt-0.5">
                        {arenaTokens > 0
                          ? `${arenaTokens} token${arenaTokens === 1 ? "" : "s"} available · no wager, no modifiers`
                          : "No tokens yet — win a wagered PvC battle to earn one"}
                      </span>
                    </span>
                    <span className={cn("relative h-4 w-7 rounded-full transition-colors shrink-0", tokenActive ? "bg-rose-400" : "bg-white/15")}>
                      <span className={cn("absolute top-0.5 h-3 w-3 rounded-full bg-black transition-all", tokenActive ? "left-3.5" : "left-0.5")} />
                    </span>
                  </button>
                )}
                {tokenActive ? (
                  <div data-testid="token-wager-locked" className="rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 font-mono text-sm text-rose-200 flex items-center gap-2">
                    <Ticket className="h-4 w-4" /> Free token battle <span className="text-white/40 text-xs">— wager disabled</span>
                  </div>
                ) : (
                <>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Wager (credits)</Label>
                  <span data-testid="wager-balance" className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 px-2.5 py-0.5 font-mono text-[11px] text-amber-200">
                    <Coins className="h-3 w-3" /> {balance.toLocaleString()} <span className="text-amber-200/50 normal-case">in wallet</span>
                  </span>
                </div>
                {wagerLocked != null ? (
                  <div className="hud-pill rounded-lg px-3 py-2 font-mono text-sm text-amber-300 flex items-center gap-2" data-testid="squad-wager-locked">
                    <Coins className="h-4 w-4" /> {wagerLocked.toLocaleString()} <span className="text-white/40 text-xs">(set by the challenger)</span>
                  </div>
                ) : (
                  <>
                    <Input data-testid="squad-wager-input" type="number" min={0} max={maxWager} value={wager}
                      onChange={(e) => setWager(e.target.value)} className="bg-[hsl(220_18%_12%)] border-white/10" />
                    <div className="flex items-center gap-1.5 mt-1.5">
                      {[100, 500, 1000].map((n) => (
                        <button key={n} type="button" disabled={n > maxWager} onClick={() => setWager(n)}
                          data-testid={`wager-quick-${n}`}
                          className="rounded-md border border-white/12 bg-white/[0.04] px-2 py-0.5 text-[11px] font-mono text-white/60 hover:text-white hover:border-white/30 disabled:opacity-30 transition-colors">
                          {n.toLocaleString()}
                        </button>
                      ))}
                      <button type="button" onClick={() => setWager(maxWager)} disabled={!maxWager}
                        data-testid="wager-max"
                        className="rounded-md border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[11px] font-mono text-amber-200 hover:bg-amber-400/20 disabled:opacity-30 transition-colors">
                        Max
                      </button>
                    </div>
                    {showDifficulty && (
                      <div className="text-[10px] text-white/40 mt-1 font-mono">{selectedDiff?.name} wager: {PVC_MIN_WAGER.toLocaleString()}–{PVC_WAGER_CAP.toLocaleString()}c (or 0 to practice). Outside this range disables Start.</div>
                    )}
                    {wagerBelowMin && (
                      <div data-testid="wager-below-min" className="text-[11px] text-rose-300 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> {selectedDiff?.name} starts at {PVC_MIN_WAGER.toLocaleString()}c — drop to 0 for a practice run or bump the wager up.
                      </div>
                    )}
                    {wagerOverMax && (
                      <div data-testid="wager-over-max" className="text-[11px] text-rose-300 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> {selectedDiff?.name} caps wagers at {PVC_WAGER_CAP.toLocaleString()}c — lower the wager or pick a tougher tier.
                      </div>
                    )}
                    {Number(wager) > balance && (
                      <div data-testid="wager-insufficient" className="text-[11px] text-rose-300 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> That's more than your wallet holds.
                      </div>
                    )}
                  </>
                )}
                {feeNote && <div className="text-[11px] text-white/40 mt-1">{feeNote}</div>}
                </>
                )}
              </div>
            )}

            {showStake && (
              <div className="pt-1" data-testid="stake-section">
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">
                  Wager cards {stakeRequired != null ? `(pick exactly ${stakeRequired})` : "(optional)"}
                </Label>
                <div className="text-[11px] text-white/40 mt-0.5">
                  {stakeRequired != null
                    ? "Match the challenger's card stake. Lose and they're gone for good."
                    : "Staked cards transfer to the winner. They're locked until the battle resolves."}
                </div>
                <div className="mt-1.5 grid grid-cols-5 gap-1.5 max-h-[132px] overflow-y-auto pr-1" data-testid="stake-grid">
                  {unlockedRoster.map((c) => {
                    const on = stakeSel.includes(c.id);
                    return (
                      <button key={c.id} type="button" data-testid={`stake-pick-${c.id}`} onClick={() => toggleStake(c.id)} title={c.title}
                        className={cn("relative rounded-md overflow-hidden border aspect-[2.5/3.5] bg-black transition-all",
                          on ? "border-amber-400 ring-2 ring-amber-400/50" : "border-white/10 hover:border-white/35", `rarity-${c.rarity}`)}>
                        {c.front_image && <img src={c.front_image} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full" style={cardImageStyle(c)} draggable={false} />}
                        <div className="foil-overlay" />
                        {on && <div className="absolute inset-0 bg-amber-400/15 flex items-center justify-center"><Coins className="h-4 w-4 text-amber-300" /></div>}
                      </button>
                    );
                  })}
                  {unlockedRoster.length === 0 && <div className="col-span-5 text-center text-white/40 text-xs py-4">No free cards to wager.</div>}
                </div>
                {stakeSel.length > 0 && <div className="mt-1 font-mono text-[10px] text-amber-300">{stakeSel.length} card(s) staked</div>}
              </div>
            )}

            {/* difficulty moved under the roster (left column) for a roomier panel */}

            {/* modifiers moved under the difficulty (left column), gated by difficulty */}

            {showDifficulty ? (
              <div className="rounded-xl border border-amber-400/20 bg-gradient-to-b from-amber-400/[0.05] to-transparent p-3 mt-1 space-y-1.5" data-testid="pvc-wager-breakdown">
                <div className="flex items-center justify-between font-mono text-[11px] text-white/55">
                  <span>Base wager</span><span>{baseWager.toLocaleString()}c</span>
                </div>
                {showBuffs && buffCost !== 0 && (
                  <div className="flex items-center justify-between font-mono text-[11px]">
                    <span className="text-white/55">Modifiers</span>
                    <span className={buffCost > 0 ? "text-amber-300" : "text-emerald-300"} data-testid="pvc-modifier-adjust">{buffCost > 0 ? `+${buffCost}` : buffCost}c</span>
                  </div>
                )}
                <div className="flex items-center justify-between font-mono text-[12px] border-t border-white/10 pt-1.5">
                  <span className="text-white/70">You stake</span>
                  <span className="text-amber-300 inline-flex items-center gap-1" data-testid="pvc-effective-wager"><Coins className="h-3 w-3" />{effectiveWager.toLocaleString()}c</span>
                </div>
                <div className="rounded-lg border border-emerald-400/25 bg-emerald-400/[0.06] px-2.5 py-2 mt-1" data-testid="pvc-loot-note">
                  <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-300/90 inline-flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Win → spend your wager on loot
                  </div>
                  <div className="font-mono text-[9px] text-white/50 mt-1 flex items-center gap-1">
                    Draft <span style={{ color: RARITY_HEX[lootRarity] }} className="inline-flex items-center gap-0.5"><Package className="h-2.5 w-2.5" />{lootRarity}</span> cards / packs / tokens at half store price.
                  </div>
                </div>
                <div className="font-mono text-[9px] text-rose-300/70 flex items-center gap-1 pt-0.5">
                  <Skull className="h-3 w-3 shrink-0" /> Lose: forfeit your wager and one soldier.
                </div>
              </div>
            ) : (showBuffs || effWager > 0) && (
              <div className="font-mono text-[11px] text-white/60 flex items-center justify-between pt-1" data-testid="squad-total-cost">
                <span>Total cost</span>
                <span className="text-amber-300 inline-flex items-center gap-1"><Coins className="h-3 w-3" /> {(effWager + (showBuffs ? buffCost : 0)).toLocaleString()}c</span>
              </div>
            )}

            {/* Power cap — creator sets a ceiling on how strong joiners may be */}
            {showPowerCap && (
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 mt-1 space-y-2" data-testid="power-cap-section">
                <button type="button" data-testid="power-cap-toggle" onClick={() => setCapEnabled((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 text-left">
                  <span className="inline-flex items-center gap-1.5 text-sm text-white/80"><ShieldHalf className="h-4 w-4 text-cyan-300" /> Limit opponent power</span>
                  <span className={cn("relative h-4 w-7 rounded-full transition-colors", capEnabled ? "bg-cyan-400" : "bg-white/15")}>
                    <span className={cn("absolute top-0.5 h-3 w-3 rounded-full bg-black transition-all", capEnabled ? "left-3.5" : "left-0.5")} />
                  </span>
                </button>
                {capEnabled && (
                  <div className="space-y-1.5" data-testid="power-cap-controls">
                    <div className="flex items-center gap-2">
                      <input type="range" min="0" max="100" step="5" value={capPct} data-testid="power-cap-slider"
                        onChange={(e) => setCapPct(Number(e.target.value))} className="flex-1 accent-cyan-400" />
                      <span className="font-mono text-xs text-cyan-300 w-12 text-right">+{capPct}%</span>
                    </div>
                    <p className="font-mono text-[10px] text-white/45 leading-tight">
                      Joiners can be at most <span className="text-amber-300">{capMaxForOthers.toLocaleString()} PWR</span> ({totalPower.toLocaleString()} +{capPct}%). Stronger squads are turned away.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Power cap — joiner sees the host's ceiling and is gated if over it */}
            {powerCap && (
              <div className={cn("rounded-lg border p-2.5 mt-1 font-mono text-[11px] flex items-center gap-2",
                capExceeded ? "border-red-400/50 bg-red-500/10 text-red-300" : "border-cyan-400/30 bg-cyan-400/5 text-white/70")}
                data-testid="power-cap-banner">
                <ShieldHalf className="h-4 w-4 shrink-0" />
                {capExceeded
                  ? <span data-testid="power-cap-exceeded">Your squad is <span className="font-semibold">{totalPower.toLocaleString()} PWR</span> — over the host's cap of <span className="font-semibold">{powerCap.maxPower.toLocaleString()} PWR</span> (+{powerCap.pct}%). Swap to weaker soldiers.</span>
                  : <span>Host caps opponents at <span className="text-amber-300 font-semibold">{powerCap.maxPower.toLocaleString()} PWR</span> (+{powerCap.pct}%). Your squad: <span className="text-white font-semibold">{totalPower.toLocaleString()} PWR</span>.</span>}
              </div>
            )}

            {warnBareGear && <BareGearWarning names={bareNames} context="the Machine" className="mt-1" />}

            <Button
              data-testid="squad-confirm-button"
              disabled={selected.length !== 3 || busy || confirmExtraDisabled || !stakeOk || capExceeded || wagerOutOfRange}
              onClick={() => onConfirm({ cardIds: selected, wager: effWager, useToken: tokenActive, stakeCardIds: stakeSel, buffPicks: activeBuffSel.map((id) => ({ id, pct: buffsAvailable.find((b) => b.id === id)?.pct ?? 15 })), difficulty: selectedDiff?.id, maxPowerPct: showPowerCap && capEnabled ? capPct : null })}
              className="w-full bg-cyan-400 text-black hover:bg-cyan-300 disabled:opacity-50"
            >
              {busy ? "Entering arena…" : capExceeded ? "Squad too strong" : confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
